import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useScrollReveal } from "../../hooks/useScrollReveal";
import DriftingGhost from "../Ghost";
import { fetchLC, type LCData } from "../LeetCode/leetcodeApi";
import { AdobeIcon } from "../../assets/icons/CompanyIcons";

/* ==================== HORCRUX ITEMS — each chapter is a dark artifact ==================== */

const LOCATIONS: Record<string, LocationData> = {
  diary: {
    color: "#d4a040", accent: "#f0c860", bg: "rgba(40,30,10,0.25)", text: "#f0e0c8", muted: "#c0a878", dim: "#8a7a5a",
    name: "The Diary", icon: "📓", horcrux: "diary", sectionId: "currently", link: "/experience",
    // Dark leather book with quill and ink bleeding out
    svg: `<svg viewBox="0 0 50 70" fill="none"><rect x="10" y="8" width="30" height="54" rx="2" fill="#3a2a14" opacity="0.12"/><rect x="12" y="10" width="26" height="50" rx="1.5" fill="#2a1c0a" opacity="0.08"/><line x1="14" y1="18" x2="36" y2="18" stroke="#d4a040" stroke-width="0.4" opacity="0.08"/><line x1="14" y1="24" x2="32" y2="24" stroke="#d4a040" stroke-width="0.3" opacity="0.06"/><line x1="14" y1="30" x2="34" y2="30" stroke="#d4a040" stroke-width="0.3" opacity="0.06"/><line x1="14" y1="36" x2="28" y2="36" stroke="#d4a040" stroke-width="0.3" opacity="0.05"/><path d="M10 8 L10 62 Q10 64 12 64" stroke="#5a4020" stroke-width="1" opacity="0.1" fill="none"/><circle cx="30" cy="48" r="4" fill="#1a0a00" opacity="0.08"><animate attributeName="r" values="4;6;4" dur="4s" repeatCount="indefinite"/></circle><path d="M34 12 Q38 8 40 4 Q41 2 42 3" stroke="#d4a040" stroke-width="0.6" opacity="0.1" fill="none"/></svg>`,
  },
  locket: {
    color: "#5a8ac0", accent: "#7ab0e0", bg: "rgba(10,18,35,0.25)", text: "#d0e0f0", muted: "#90aac8", dim: "#5a7090",
    name: "The Locket", icon: "🔮", horcrux: "locket", sectionId: "expertise-ch", link: "/skills",
    // Ornate oval locket with S-serpent engraving and chain
    svg: `<svg viewBox="0 0 50 70" fill="none"><path d="M25 5 Q22 5 20 8 L20 12 Q20 14 22 14 L28 14 Q30 14 30 12 L30 8 Q28 5 25 5Z" stroke="#5a8ac0" stroke-width="0.6" opacity="0.1" fill="none"/><ellipse cx="25" cy="35" rx="14" ry="18" stroke="#5a8ac0" stroke-width="0.8" opacity="0.12" fill="#0a1420" fill-opacity="0.06"/><ellipse cx="25" cy="35" rx="11" ry="15" stroke="#7ab0e0" stroke-width="0.4" opacity="0.08" fill="none"/><path d="M22 25 Q28 30 22 35 Q28 40 22 45" stroke="#5a8ac0" stroke-width="0.8" opacity="0.1" fill="none"/><circle cx="25" cy="35" r="2" fill="#5a8ac0" opacity="0.08"><animate attributeName="opacity" values="0.08;0.15;0.08" dur="3s" repeatCount="indefinite"/></circle><line x1="25" y1="14" x2="25" y2="17" stroke="#5a8ac0" stroke-width="0.5" opacity="0.08"/></svg>`,
  },
  diadem: {
    color: "#8a5cc0", accent: "#a880e0", bg: "rgba(22,12,38,0.25)", text: "#e0d0f0", muted: "#a890c0", dim: "#7a6090",
    name: "The Diadem", icon: "👑", horcrux: "diadem", sectionId: "projects-ch", link: "/projects",
    // Elegant tiara/crown with central gemstone
    svg: `<svg viewBox="0 0 50 70" fill="none"><path d="M8 38 Q12 20 18 25 Q22 15 25 10 Q28 15 32 25 Q38 20 42 38" stroke="#8a5cc0" stroke-width="0.8" opacity="0.12" fill="none"/><path d="M10 38 Q15 22 20 26 Q23 18 25 13 Q27 18 30 26 Q35 22 40 38" stroke="#a880e0" stroke-width="0.4" opacity="0.06" fill="none"/><ellipse cx="25" cy="12" rx="3" ry="2.5" fill="#8a5cc0" opacity="0.1"><animate attributeName="opacity" values="0.1;0.18;0.1" dur="3.5s" repeatCount="indefinite"/></ellipse><path d="M8 38 Q25 42 42 38" stroke="#8a5cc0" stroke-width="0.6" opacity="0.1" fill="none"/><circle cx="18" cy="24" r="1.5" fill="#a880e0" opacity="0.06"/><circle cx="32" cy="24" r="1.5" fill="#a880e0" opacity="0.06"/><path d="M12 40 Q25 44 38 40" stroke="#8a5cc0" stroke-width="0.3" opacity="0.06" fill="none"/></svg>`,
  },
  cup: {
    color: "#c04040", accent: "#e06060", bg: "rgba(35,10,10,0.25)", text: "#f0d0d0", muted: "#c09090", dim: "#8a5a5a",
    name: "The Cup", icon: "🏆", horcrux: "cup", sectionId: "honours-ch", link: "/leetcode",
    // Golden two-handled goblet with badger engraving
    svg: `<svg viewBox="0 0 50 70" fill="none"><path d="M18 15 Q18 8 25 8 Q32 8 32 15" stroke="#c04040" stroke-width="0.8" opacity="0.1" fill="#2a0a0a" fill-opacity="0.04"/><rect x="18" y="15" width="14" height="22" rx="2" stroke="#c04040" stroke-width="0.6" opacity="0.1" fill="none"/><path d="M14 18 Q8 22 10 30 Q12 34 18 32" stroke="#c04040" stroke-width="0.7" opacity="0.08" fill="none"/><path d="M36 18 Q42 22 40 30 Q38 34 32 32" stroke="#c04040" stroke-width="0.7" opacity="0.08" fill="none"/><rect x="21" y="37" width="8" height="10" rx="1" stroke="#c04040" stroke-width="0.4" opacity="0.06" fill="none"/><ellipse cx="25" cy="50" rx="10" ry="3" stroke="#c04040" stroke-width="0.6" opacity="0.08" fill="none"/><circle cx="25" cy="25" r="3" fill="#c04040" opacity="0.05"><animate attributeName="opacity" values="0.05;0.12;0.05" dur="4s" repeatCount="indefinite"/></circle></svg>`,
  },
  ring: {
    color: "#40a0a0", accent: "#60c8c8", bg: "rgba(10,28,28,0.25)", text: "#d0f0f0", muted: "#90b8b8", dim: "#5a8080",
    name: "The Ring", icon: "💍", horcrux: "ring", sectionId: "blogs-ch", link: "/blogs",
    // Cracked stone ring with triangle symbol
    svg: `<svg viewBox="0 0 50 70" fill="none"><ellipse cx="25" cy="35" rx="14" ry="16" stroke="#40a0a0" stroke-width="0.8" opacity="0.12" fill="none"/><ellipse cx="25" cy="35" rx="10" ry="12" stroke="#40a0a0" stroke-width="0.4" opacity="0.06" fill="none"/><rect x="19" y="18" width="12" height="10" rx="2" fill="#0a1a1a" fill-opacity="0.08" stroke="#60c8c8" stroke-width="0.5" opacity="0.1"/><polygon points="25,19 31,27 19,27" stroke="#60c8c8" stroke-width="0.5" opacity="0.12" fill="none"/><line x1="20" y1="22" x2="30" y2="24" stroke="#40a0a0" stroke-width="0.3" opacity="0.08"/><circle cx="25" cy="23" r="1" fill="#60c8c8" opacity="0.08"><animate attributeName="opacity" values="0.08;0.16;0.08" dur="3s" repeatCount="indefinite"/></circle></svg>`,
  },
  snake: {
    color: "#60a060", accent: "#80c880", bg: "rgba(12,28,12,0.25)", text: "#d8f0d8", muted: "#98b898", dim: "#5a7a5a",
    name: "The Serpent", icon: "🐍", horcrux: "snake", sectionId: "contact-ch", link: "/contact",
    // Coiled serpent with glowing eyes
    svg: `<svg viewBox="0 0 50 70" fill="none"><path d="M25 12 Q35 14 38 22 Q40 30 35 38 Q28 46 20 42 Q12 38 15 28 Q18 20 25 22 Q30 24 28 30 Q26 34 22 32" stroke="#60a060" stroke-width="0.8" opacity="0.12" fill="none"/><circle cx="25" cy="12" r="4" fill="#1a2a1a" fill-opacity="0.06" stroke="#60a060" stroke-width="0.5" opacity="0.1"/><circle cx="23" cy="11" r="1" fill="#80c880" opacity="0.12"><animate attributeName="opacity" values="0.12;0.22;0.12" dur="2.5s" repeatCount="indefinite"/></circle><circle cx="27" cy="11" r="1" fill="#80c880" opacity="0.12"><animate attributeName="opacity" values="0.12;0.22;0.12" dur="2.5s" repeatCount="indefinite" begin="0.3s"/></circle><path d="M24 15 L25 17 L26 15" stroke="#60a060" stroke-width="0.4" opacity="0.08" fill="none"/></svg>`,
  },
};

interface LocationData { color: string; accent: string; bg: string; text: string; muted: string; dim: string; name: string; icon: string; svg: string; horcrux: string; sectionId: string; link: string; }

/* ==================== MARAUDER'S MAP INTRO ==================== */

// Animated footprints walking across the map
const Footprints: React.FC = () => (
  <FootprintWrap>
    {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
      <Footprint key={i} $i={i} $left={i % 2 === 0}>
        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
          <ellipse cx="6" cy="10" rx="3.5" ry="5" fill="rgba(160,120,60,0.35)"/>
          <circle cx="4" cy="3.5" r="1.3" fill="rgba(160,120,60,0.3)"/>
          <circle cx="6.5" cy="2.5" r="1.2" fill="rgba(160,120,60,0.3)"/>
          <circle cx="8.5" cy="4" r="1.1" fill="rgba(160,120,60,0.25)"/>
          <circle cx="2.5" cy="5" r="1" fill="rgba(160,120,60,0.25)"/>
        </svg>
      </Footprint>
    ))}
  </FootprintWrap>
);

// Deathly Hallows symbol
const DeathlyHallows: React.FC = () => (
  <HallowsWrap>
    <svg width="50" height="50" viewBox="0 0 50 50" fill="none">
      <polygon points="25,2 48,48 2,48" stroke="rgba(180,150,80,0.2)" strokeWidth="0.8" fill="none"/>
      <circle cx="25" cy="32" r="10" stroke="rgba(180,150,80,0.15)" strokeWidth="0.7" fill="none"/>
      <line x1="25" y1="2" x2="25" y2="48" stroke="rgba(180,150,80,0.15)" strokeWidth="0.7"/>
    </svg>
  </HallowsWrap>
);

/* ==================== CHAPTER COMPONENT ==================== */

const Chapter: React.FC<{
  num: string; tag: string; title: string; children: React.ReactNode; id?: string;
  location: LocationData;
}> = ({ num, tag, title, children, id, location: loc }) => {
  const { ref, isVisible } = useScrollReveal(0.1);
  const navigate = useNavigate();
  return (
    <ChapterSection ref={ref} $visible={isVisible} id={id} $bg={loc.bg} $color={loc.color} $horcrux={loc.horcrux}
      onClick={() => navigate(loc.link)}
    >
      {/* Horcrux visual overlay */}
      <HorcruxOverlay $horcrux={loc.horcrux} $color={loc.color} />
      {/* Location silhouette on the right side */}
      <LocationSilhouette dangerouslySetInnerHTML={{ __html: loc.svg }} />
      {/* Location badge top-right */}
      <LocationBadge $color={loc.color}>
        <span>{loc.icon}</span>
        <LocationBadgeName>{loc.name}</LocationBadgeName>
      </LocationBadge>
      <ChapterContent>
        <ChapterNum $color={loc.color}>{num}</ChapterNum>
        <ChapterTag $color={loc.color}>{tag}</ChapterTag>
        <ChapterTitle $color={loc.accent}>{title}</ChapterTitle>
        <ChapterBody $text={loc.text} $muted={loc.muted} $dim={loc.dim} $color={loc.color} $accent={loc.accent}>
          {children}
        </ChapterBody>
      </ChapterContent>
    </ChapterSection>
  );
};

/* ==================== HOME PAGE ==================== */

const CHAPTER_LIST = [
  { num: "01", tag: "CURRENTLY", icon: "📓", color: LOCATIONS.diary.color },
  { num: "02", tag: "EXPERTISE", icon: "🔮", color: LOCATIONS.locket.color },
  { num: "03", tag: "PROJECTS", icon: "👑", color: LOCATIONS.diadem.color },
  { num: "04", tag: "HONOURS", icon: "🏆", color: LOCATIONS.cup.color },
  { num: "05", tag: "BLOGS", icon: "💍", color: LOCATIONS.ring.color },
  { num: "06", tag: "CONTACT", icon: "🐍", color: LOCATIONS.snake.color },
];

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DAYS_SHORT = ["","Mon","","Wed","","Fri",""];

const HomePage: React.FC = () => {
  const [activeChapter, setActiveChapter] = useState(0);
  const [showRoller, setShowRoller] = useState(false);
  const [lcData, setLcData] = useState<LCData | null>(null);
  const [sworn, setSworn] = useState(false);

  useEffect(() => { fetchLC().then(setLcData); }, []);
  // Track which chapter is visible via IntersectionObserver
  useEffect(() => {
    const ids = ["currently", "expertise-ch", "projects-ch", "honours-ch", "blogs-ch", "contact-ch"];
    const observers: IntersectionObserver[] = [];

    ids.forEach((id, i) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveChapter(i); },
        { threshold: 0.3 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, []);

  // Show roller when map section is in view
  useEffect(() => {
    const el = document.getElementById("map-marker");
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowRoller(entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Auto-reveal the map text after a delay
  useEffect(() => {
    const t = setTimeout(() => setSworn(true), 2000);
    return () => clearTimeout(t);
  }, []);

  const scrollToChapter = useCallback((i: number) => {
    const ids = ["currently", "expertise-ch", "projects-ch", "honours-ch", "blogs-ch", "contact-ch"];
    document.getElementById(ids[i])?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  return (
  <HomeWrap>
    {/* Marker for roller visibility */}
    <div id="map-marker" />

    {/* ═══ Marauder's Map — parchment intro ═══ */}
    <MapSection>
      <MapParchment />
      <MapInkStains>
        <InkBlot $x={8} $y={15} $size={120} />
        <InkBlot $x={82} $y={70} $size={90} />
        <InkBlot $x={45} $y={85} $size={70} />
      </MapInkStains>
      <Footprints />
      <MapCard>
        <MapCardInner>
          <DeathlyHallows />
          <SolemnText $visible={!sworn}>I solemnly swear that I am up to no good.</SolemnText>
          <MapTitle $visible={sworn}>The Marauder's Map</MapTitle>
          <MapSub $visible={sworn}>Messrs. Moony, Wormtail, Padfoot, and Prongs are proud to present...</MapSub>
          <LocationStrip $visible={sworn}>
            {Object.values(LOCATIONS).map((loc) => (
              <LocationDot key={loc.name} $color={loc.color} title={loc.name}
                onClick={() => document.getElementById(loc.sectionId)?.scrollIntoView({ behavior: "smooth", block: "center" })}
              >{loc.icon}</LocationDot>
            ))}
          </LocationStrip>
        </MapCardInner>
      </MapCard>
    </MapSection>

    {/* ── Chapter 01 ── */}
    {/* Chapters 01–06, haunted */}
    <Chapters>
      <ChapterHaunt aria-hidden="true">
        <DriftingGhost name="The Grey Lady" duration={26} band={[5, 13]} />
        <DriftingGhost name="The Fat Friar" duration={30} delay={5} band={[45, 55]} />
        <DriftingGhost name="The Bloody Baron" duration={28} delay={10} band={[78, 88]} />
      </ChapterHaunt>

    <Chapter num="01" tag="CURRENTLY" title="Where I Build" id="currently" location={LOCATIONS.diary}>
      <CurrentCard>
        <CurrentLogo><AdobeIcon size={34} /></CurrentLogo>
        <CurrentInfo>
          <CurrentCompany>Adobe</CurrentCompany>
          <CurrentRole>Member of Technical Staff 2</CurrentRole>
          <CurrentTeam>Document Cloud · Noida, India</CurrentTeam>
          <CurrentHighlight>Previously Sr. Software Engineer at Infoedge — 200K+ users · 10+ production features · INP ↓38% · CLS ↓95%</CurrentHighlight>
        </CurrentInfo>
        <LiveBadge><LiveDot />Present</LiveBadge>
      </CurrentCard>
      <GlimpseFooter><GlimpseCount>6 more roles</GlimpseCount><SeeMore to="/experience">See full journey →</SeeMore></GlimpseFooter>
    </Chapter>

    {/* ── Chapter 02 ── */}
    <Chapter num="02" tag="EXPERTISE" title="Spell Arsenal" location={LOCATIONS.locket} id="expertise-ch">
      <ExpertiseGrid>
        {[
          { icon: "⟨/⟩", title: "Frontend Engineering", desc: "React, TypeScript, React Native — UIs at scale" },
          { icon: "⚙", title: "Backend & APIs", desc: "Node.js, Java, SpringBoot, Kafka — systems that handle load" },
        ].map((e) => (
          <ExpertiseCard key={e.title}><ExpIcon>{e.icon}</ExpIcon><ExpTitle>{e.title}</ExpTitle><ExpDesc>{e.desc}</ExpDesc></ExpertiseCard>
        ))}
      </ExpertiseGrid>
      <GlimpseFooter><GlimpseCount>25 skills across 4 categories</GlimpseCount><SeeMore to="/skills">See all skills →</SeeMore></GlimpseFooter>
    </Chapter>

    {/* ── Chapter 03 ── */}
    <Chapter num="03" tag="PROJECTS" title="What I've Built" location={LOCATIONS.diadem} id="projects-ch">
      <ProjectPreview>
        <ProjectPreviewTitle>Productive Weekday</ProjectPreviewTitle>
        <ProjectPreviewDesc>Visualizes GitHub contributions as a racebar graph to find your most productive weekday.</ProjectPreviewDesc>
        <ProjectPreviewTech>{["React", "Redux", "MongoDB", "d3.js"].map(t => <TechPill key={t}>{t}</TechPill>)}</ProjectPreviewTech>
        <ProjectPreviewLinks>
          <ProjectLink href="https://github.com/jugshaurya/productive-weekday" target="_blank" rel="noopener noreferrer">GitHub ↗</ProjectLink>
          <ProjectLink href="https://productive-weekday.netlify.app/" target="_blank" rel="noopener noreferrer" $accent>Live ↗</ProjectLink>
        </ProjectPreviewLinks>
      </ProjectPreview>
      <GlimpseFooter><GlimpseCount>7 more projects</GlimpseCount><SeeMore to="/projects">See all projects →</SeeMore></GlimpseFooter>
    </Chapter>

    {/* ── Chapter 04 ── */}
    <Chapter num="04" tag="HONOURS" title="Achievement Unlocked" location={LOCATIONS.cup} id="honours-ch">
      <AwardsList>
        {[
          { title: "Individual Excellence Award", org: "Infoedge", year: "Jan 2025", icon: "🏆" },
          { title: "GATE'21 — AIR 2547 (Top 3%)", org: "IIT Bombay", year: "2021", icon: "🎓" },
          { title: "LeetCode Guardian (2244)", org: "LeetCode", year: "2023", icon: "🛡" },
        ].map((a, i) => (
          <AwardItem key={a.title} $i={i}><AwardIcon>{a.icon}</AwardIcon><AwardContent><AwardTitle>{a.title}</AwardTitle><AwardMeta>{a.org} · {a.year}</AwardMeta></AwardContent></AwardItem>
        ))}
      </AwardsList>
      <GlimpseFooter><GlimpseCount>5 more achievements</GlimpseCount><SeeMore to="/leetcode">See LeetCode stats →</SeeMore></GlimpseFooter>
    </Chapter>

    {/* ── Chapter 05 ── */}
    <Chapter num="05" tag="PUBLICATIONS" title="I Write Too" location={LOCATIONS.ring} id="blogs-ch">
      <BlogCard href="https://medium.com/@shauryasinghal84/measuring-the-performance-of-react-native-apps-77741b199ce7" target="_blank" rel="noopener noreferrer">
        <BlogTag style={{ background: "rgba(64,160,160,0.12)", color: "#60c8c8" }}>Performance</BlogTag>
        <BlogTitle>Measuring Performance of React Native vs Flutter</BlogTitle>
        <BlogDesc>FPS, CPU, Memory comparison across cross-platform frameworks</BlogDesc>
        <BlogLink style={{ color: "#60c8c8" }}>Read on Medium ↗</BlogLink>
      </BlogCard>
      <GlimpseFooter><GlimpseCount>2 more articles</GlimpseCount><SeeMoreExt href="https://medium.com/@shauryasinghal84" target="_blank" rel="noopener noreferrer">All posts on Medium →</SeeMoreExt></GlimpseFooter>
    </Chapter>

    {/* ── LeetCode Activity Heatmap ── */}
    {lcData && <ActivitySection cal={lcData.calendar} total={lcData.totalSubmissions} />}

    {/* ── Chapter 06 ── */}
    <Chapter num="06" tag="CONTACT" title="Send an Owl" location={LOCATIONS.snake} id="contact-ch">
      <ContactText>Open to connecting — collaboration, opportunities, or competitive programming.</ContactText>
      <ContactButtons>
        <ContactBtn href="mailto:shauryasinghal84@gmail.com">✉ shauryasinghal84@gmail.com</ContactBtn>
        <ContactBtn href="https://www.linkedin.com/in/jugshaurya/" target="_blank" rel="noopener noreferrer" $secondary>LinkedIn →</ContactBtn>
      </ContactButtons>
      <SeeMore to="/contact">Full contact page →</SeeMore>
    </Chapter>
    </Chapters>

    {/* Cylinder roller — bottom right */}
    {showRoller && (
      <RollerWrap>
        <RollerWindow>
          {CHAPTER_LIST.map((ch, i) => {
            const offset = i - activeChapter;
            const isActive = offset === 0;
            const isNear = Math.abs(offset) === 1;
            const isVisible = Math.abs(offset) <= 1;
            return (
              <RollerItem
                key={ch.num}
                $offset={offset}
                $active={isActive}
                $near={isNear}
                $visible={isVisible}
                $color={ch.color}
                onClick={() => scrollToChapter(i)}
              >
                <RollerEmoji>{ch.icon}</RollerEmoji>
                <RollerLabel $active={isActive}>{ch.tag}</RollerLabel>
                <RollerNum>{ch.num}</RollerNum>
              </RollerItem>
            );
          })}
        </RollerWindow>
      </RollerWrap>
    )}
  </HomeWrap>
  );
};

/* ==================== ACTIVITY HEATMAP ==================== */

const ActivitySection: React.FC<{ cal: Record<string, number>; total: number }> = ({ cal, total }) => {
  const { ref, isVisible } = useScrollReveal(0.1);

  const { cells, monthLabels } = useMemo(() => {
    const now = new Date();
    const days: { x: number; y: number; count: number; date: string }[] = [];
    const months: { label: string; x: number }[] = [];
    const step = 15;
    let lastMonth = -1;

    /* LeetCode keys its calendar by UTC midnight. Building the key from
       `d.getTime()` carried the current time of day, so no key ever matched
       and every cell read zero. Columns are also anchored to real Sun–Sat
       weeks — `index / 7` skews the grid unless the window starts on a Sunday. */
    const DAY = 86400000;
    const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const windowStart = todayUTC - 364 * DAY;
    const gridStart = windowStart - new Date(windowStart).getUTCDay() * DAY;

    for (let t = gridStart; t <= todayUTC; t += DAY) {
      if (t < windowStart) continue;            // padding up to the first Sunday
      const d = new Date(t);
      const week = Math.round((t - gridStart) / (7 * DAY));
      const count = cal[String(Math.floor(t / 1000))] || 0;
      days.push({
        x: week * step,
        y: d.getUTCDay() * step,
        count,
        date: d.toISOString().slice(0, 10),
      });

      if (d.getUTCMonth() !== lastMonth) {
        lastMonth = d.getUTCMonth();
        months.push({ label: MONTHS_SHORT[d.getUTCMonth()], x: week * step });
      }
    }
    return { cells: days, monthLabels: months };
  }, [cal]);

  const counts = useMemo(() => Object.values(cal).filter(v => v > 0), [cal]);
  const max = Math.max(8, ...counts);
  const W = 53 * 15;

  return (
    <ActWrap ref={ref} $visible={isVisible}>
      <ActHeader>
        <ActLeft>
          <ActTag>ACTIVITY</ActTag>
          <ActTitle>LeetCode Contributions</ActTitle>
        </ActLeft>
        <ActRight>
          ⊙ <b>{total.toLocaleString()}</b> submissions in the last year
        </ActRight>
      </ActHeader>

      <HeatmapScroll>
        <ActSvg viewBox={`-35 -5 ${W + 40} 130`}>
          {monthLabels.map((m, i) => (
            <text key={i} x={m.x} y={-1} fill="#6a5a4a" fontSize="9" fontFamily="JetBrains Mono">{m.label}</text>
          ))}
          {DAYS_SHORT.map((d, i) => (
            d ? <text key={i} x={-30} y={i * 15 + 18} fill="#6a5a4a" fontSize="8" fontFamily="JetBrains Mono" textAnchor="end">{d}</text> : null
          ))}
          {cells.map((c, i) => (
            <circle key={i} cx={c.x + 5.5} cy={c.y + 11} r="5"
              fill={c.count === 0 ? "rgba(255,255,255,0.04)" : "#d4a040"}
              opacity={c.count === 0 ? 1 : Math.min(1, 0.25 + (c.count / max) * 0.75)}
            >
              <title>{c.date}: {c.count} submissions</title>
            </circle>
          ))}
          <text x={W - 100} y={120} fill="#6a5a4a" fontSize="8" fontFamily="JetBrains Mono">Less</text>
          {[0, 0.2, 0.4, 0.7, 1].map((t, i) => (
            <circle key={`l${i}`} cx={W - 65 + i * 14} cy={117} r="5"
              fill={t === 0 ? "rgba(255,255,255,0.04)" : "#d4a040"}
              opacity={t === 0 ? 1 : 0.25 + t * 0.75}
            />
          ))}
          <text x={W + 5} y={120} fill="#6a5a4a" fontSize="8" fontFamily="JetBrains Mono">More</text>
        </ActSvg>
      </HeatmapScroll>

      <ActFooter>
        <SeeMore to="/leetcode">View full LeetCode stats →</SeeMore>
      </ActFooter>
    </ActWrap>
  );
};

export default HomePage;

/* ===================== ANIMATIONS ===================== */
const fadeUp = keyframes`from{opacity:0;transform:translateY(25px)}to{opacity:1;transform:translateY(0)}`;
const livePulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(212,160,64,0.4)}50%{box-shadow:0 0 0 6px rgba(212,160,64,0)}`;
const locationFloat = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}`;
const locationGlow = keyframes`0%,100%{opacity:0.4;filter:blur(0px)}50%{opacity:0.7;filter:blur(1px)}`;
const inkReveal = keyframes`from{opacity:0;letter-spacing:0.5em;filter:blur(2px)}to{opacity:1;letter-spacing:0.2em;filter:blur(0)}`;
const inkFadeOut = keyframes`from{opacity:1}to{opacity:0;filter:blur(3px)}`;
const hallowsSpin = keyframes`0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}`;
const stepAppear = keyframes`0%{opacity:0;transform:scale(0.3)}30%{opacity:0.5;transform:scale(1)}100%{opacity:0;transform:scale(1)}`;

/* ===================== LAYOUT ===================== */
const HomeWrap = styled.div`
  position: relative;
  background: #0e0c14;
  overflow: hidden;
`;

/* ═══ MARAUDER'S MAP SECTION — parchment-like, warm tones ═══ */

const MapSection = styled.div`
  position: relative;
  min-height: 100vh;
  background: #0c0a08;
  overflow: hidden;
`;

const MapParchment = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    radial-gradient(ellipse at 30% 40%, rgba(120,90,40,0.06), transparent 50%),
    radial-gradient(ellipse at 70% 60%, rgba(100,80,30,0.04), transparent 50%),
    radial-gradient(ellipse at 50% 50%, rgba(80,60,20,0.03), transparent 70%);
  /* Subtle parchment texture via noise-like pattern */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      radial-gradient(1px 1px at 10% 15%, rgba(180,150,80,0.15), transparent),
      radial-gradient(1px 1px at 30% 55%, rgba(180,150,80,0.1), transparent),
      radial-gradient(1.5px 1.5px at 50% 25%, rgba(180,150,80,0.08), transparent),
      radial-gradient(1px 1px at 70% 70%, rgba(180,150,80,0.12), transparent),
      radial-gradient(1px 1px at 90% 40%, rgba(180,150,80,0.1), transparent),
      radial-gradient(1px 1px at 20% 80%, rgba(180,150,80,0.08), transparent),
      radial-gradient(1px 1px at 60% 10%, rgba(180,150,80,0.1), transparent),
      radial-gradient(1.5px 1.5px at 80% 90%, rgba(180,150,80,0.07), transparent),
      radial-gradient(1px 1px at 40% 45%, rgba(180,150,80,0.09), transparent),
      radial-gradient(1px 1px at 15% 35%, rgba(180,150,80,0.06), transparent);
  }
  /* Map grid lines — like the Marauder's Map corridors */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(140,110,50,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(140,110,50,0.025) 1px, transparent 1px);
    background-size: 80px 80px;
  }
`;

const MapInkStains = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const InkBlot = styled.div<{$x:number;$y:number;$size:number}>`
  position: absolute;
  left: ${({$x})=>$x}%;
  top: ${({$y})=>$y}%;
  width: ${({$size})=>$size}px;
  height: ${({$size})=>$size}px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(80,60,20,0.06), transparent 70%);
  filter: blur(30px);
`;

const FootprintWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;
`;

const Footprint = styled.div<{$i:number;$left:boolean}>`
  position: absolute;
  left: ${({$i})=> 20 + ($i * 8)}%;
  top: ${({$i})=> 25 + (($i % 3) * 18)}%;
  transform: rotate(${({$left})=>$left ? "-10deg" : "10deg"});
  animation: ${stepAppear} 3s ease-in-out infinite;
  animation-delay: ${({$i})=>$i * 0.6}s;
  opacity: 0;
`;

const HallowsWrap = styled.div`
  animation: ${hallowsSpin} 40s linear infinite;
  opacity: 0.6;
`;

const MapCard = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 4rem 2rem;
`;

const MapCardInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  text-align: center;
`;

const SolemnText = styled.p<{$visible:boolean}>`
  font-family: "Cinzel", serif;
  font-size: clamp(0.7rem, 1.8vw, 1rem);
  font-style: italic;
  color: rgba(180,150,80,0.6);
  letter-spacing: 0.2em;
  animation: ${({$visible})=>$visible ? inkReveal : inkFadeOut} ${({$visible})=>$visible ? "2s" : "0.8s"} ease both;
`;

const MapTitle = styled.h2<{$visible:boolean}>`
  font-family: "Cinzel", serif;
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 900;
  color: #c8a050;
  letter-spacing: 0.08em;
  text-shadow: 0 0 30px rgba(180,140,50,0.2), 0 0 60px rgba(180,140,50,0.05);
  text-align: center;
  margin: 0.5rem 0;
  opacity: ${({$visible})=>$visible ? 1 : 0};
  transform: translateY(${({$visible})=>$visible ? 0 : "15px"});
  transition: all 1s ease 0.3s;
`;

const MapSub = styled.p<{$visible:boolean}>`
  font-family: "Cinzel", serif;
  font-size: 0.6rem;
  font-style: italic;
  color: #5a4a30;
  letter-spacing: 0.08em;
  max-width: 450px;
  text-align: center;
  opacity: ${({$visible})=>$visible ? 1 : 0};
  transition: opacity 1s ease 0.8s;
`;

const LocationStrip = styled.div<{$visible:boolean}>`
  display: flex;
  gap: 0.6rem;
  opacity: ${({$visible})=>$visible ? 1 : 0};
  transition: opacity 0.8s ease 1.2s;
`;

const LocationDot = styled.div<{$color: string}>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({$color}) => $color}0a;
  border: 1px solid ${({$color}) => $color}20;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  cursor: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: ${({$color}) => $color}60;
    background: ${({$color}) => $color}15;
    box-shadow: 0 4px 15px ${({$color}) => $color}20;
  }
`;

/* ===================== CHAPTER (Horcrux-item themed boxes) ===================== */

const horcruxStyles: Record<string, string> = {
  // Diary: aged leather book — page-like border, torn edges, ink corner stains
  diary: `
    border-radius: 4px 12px 12px 4px;
    border-left: 4px solid rgba(90,60,20,0.5);
    border-right: 1px solid rgba(160,120,50,0.12);
    border-top: 1px solid rgba(160,120,50,0.1);
    border-bottom: 1px solid rgba(160,120,50,0.1);
    box-shadow: inset 3px 0 12px rgba(60,40,10,0.15), inset -2px 0 8px rgba(60,40,10,0.05);
  `,
  // Locket: ornate oval frame — double border, chain-link top
  locket: `
    border-radius: 50% / 16px;
    border: 1px solid rgba(90,138,192,0.15);
    box-shadow: 0 0 0 3px rgba(10,18,35,0.3), 0 0 0 5px rgba(90,138,192,0.08);
  `,
  // Diadem: crown/tiara — pointed top border, jeweled corners
  diadem: `
    border-radius: 2px 2px 16px 16px;
    border: 1px solid rgba(138,92,192,0.12);
    border-top: 2px solid rgba(138,92,192,0.25);
    clip-path: polygon(0% 4%, 15% 0%, 30% 3%, 50% 0%, 70% 3%, 85% 0%, 100% 4%, 100% 100%, 0% 100%);
  `,
  // Cup: goblet — wide top, tapered bottom, golden rim
  cup: `
    border-radius: 20px 20px 8px 8px;
    border: 1px solid rgba(192,64,64,0.12);
    border-top: 2px solid rgba(192,64,64,0.2);
    box-shadow: inset 0 2px 0 rgba(192,64,64,0.06);
  `,
  // Ring: circular/rounded — thick rounded border, cracked effect
  ring: `
    border-radius: 24px;
    border: 2px solid rgba(64,160,160,0.12);
    box-shadow: 0 0 0 4px rgba(10,28,28,0.3), 0 0 0 6px rgba(64,160,160,0.06);
  `,
  // Snake: sinuous — wavy/serpentine, asymmetric border
  snake: `
    border-radius: 16px 4px 16px 4px;
    border: 1px solid rgba(96,160,96,0.12);
    border-left: 3px solid rgba(96,160,96,0.18);
    border-right: 3px solid rgba(96,160,96,0.08);
  `,
};

const ChapterSection = styled.div<{$visible:boolean;$bg:string;$color:string;$horcrux:string}>`
  position: relative;
  z-index: 1;
  max-width: 820px;
  margin: 0 auto 2.5rem;
  padding: 2.5rem 2rem 2.5rem 2.5rem;
  overflow: hidden;
  opacity: ${({$visible}) => $visible ? 1 : 0};
  animation: ${({$visible}) => $visible ? fadeUp : "none"} 0.6s ease both;
  transition: all 0.4s ease;
  cursor: pointer;

  background: ${({$bg}) => $bg};
  backdrop-filter: blur(12px);

  /* Per-horcrux visual shape */
  ${({$horcrux}) => horcruxStyles[$horcrux] || `border-radius:16px;border:1px solid rgba(255,255,255,0.06);`}

  &:hover {
    border-color: ${({$color}) => $color}25;
    transform: translateY(-2px);
    box-shadow: 0 8px 30px ${({$color}) => $color}10;
  }
`;

/* Horcrux overlay — unique visual decoration per artifact */
const horcruxOverlayCSS: Record<string, string> = {
  // Diary: ink bleeding from top-right corner, faint ruled lines
  diary: `
    background:
      repeating-linear-gradient(180deg, transparent, transparent 28px, rgba(160,120,50,0.03) 28px, rgba(160,120,50,0.03) 29px),
      radial-gradient(ellipse at 90% 5%, rgba(20,10,0,0.12), transparent 40%);
    &::after {
      content: "";
      position: absolute;
      left: 18px;
      top: 0;
      bottom: 0;
      width: 1px;
      background: rgba(180,60,60,0.06);
    }
  `,
  // Locket: radial S-chain glow at center
  locket: `
    background: radial-gradient(ellipse at 50% 50%, rgba(90,138,192,0.04), transparent 60%);
    &::after {
      content: "S";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%,-50%);
      font-family: "Cinzel", serif;
      font-size: 8rem;
      font-style: italic;
      color: rgba(90,138,192,0.025);
      pointer-events: none;
    }
  `,
  // Diadem: radial gem glow at top center
  diadem: `
    background:
      radial-gradient(ellipse at 50% 0%, rgba(138,92,192,0.06), transparent 40%),
      radial-gradient(circle at 50% 2%, rgba(200,160,255,0.04), transparent 15%);
  `,
  // Cup: warm glow from within, golden rim highlight
  cup: `
    background:
      linear-gradient(180deg, rgba(192,64,64,0.03) 0%, transparent 20%),
      radial-gradient(ellipse at 50% 30%, rgba(192,100,40,0.04), transparent 50%);
  `,
  // Ring: circular crack lines radiating from center stone
  ring: `
    background: radial-gradient(circle at 50% 30%, rgba(96,200,200,0.03), transparent 40%);
    &::after {
      content: "";
      position: absolute;
      top: 25%;
      left: 48%;
      width: 30px;
      height: 30px;
      border: 1px solid rgba(96,200,200,0.04);
      transform: rotate(45deg);
      pointer-events: none;
    }
  `,
  // Snake: diagonal scale-like pattern
  snake: `
    background:
      repeating-linear-gradient(135deg, transparent, transparent 20px, rgba(96,160,96,0.015) 20px, rgba(96,160,96,0.015) 21px),
      repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(96,160,96,0.01) 20px, rgba(96,160,96,0.01) 21px);
  `,
};

const HorcruxOverlay = styled.div<{$horcrux:string;$color:string}>`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  ${({$horcrux}) => horcruxOverlayCSS[$horcrux] || ""}
`;

const Chapters = styled.div`position:relative;padding-top:40px;`;

/* Overlay spanning every chapter, so the ghosts can drift the whole way down. */
const ChapterHaunt = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 3;
`;

const ChapterContent = styled.div`position:relative;z-index:1;`;

const ChapterNum = styled.span<{$color:string}>`
  font-family: "Cinzel", serif;
  font-size: 2.5rem;
  font-weight: 900;
  color: ${({$color}) => $color}12;
  letter-spacing: -0.02em;
  display: block;
  line-height: 1;
  margin-bottom: 0.3rem;
  user-select: none;
`;

const ChapterTag = styled.p<{$color:string}>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: ${({$color}) => $color};
  letter-spacing: 0.25em;
  margin-bottom: 0.2rem;
  text-transform: uppercase;
`;

const ChapterTitle = styled.h2<{$color:string}>`
  font-family: "Cinzel", serif;
  font-size: clamp(1.3rem, 3vw, 2rem);
  font-weight: 700;
  color: #e8e4e0;
  margin-bottom: 1.2rem;

  &::after {
    content: "";
    display: block;
    width: 50px;
    height: 3px;
    background: ${({$color}) => $color};
    margin-top: 0.5rem;
    border-radius: 2px;
    box-shadow: 0 0 10px ${({$color}) => $color}40;
  }
`;

const ChapterBody = styled.div<{$text:string;$muted:string;$dim:string;$color:string;$accent:string}>`
  --ch-text:${({$text})=>$text};
  --ch-muted:${({$muted})=>$muted};
  --ch-dim:${({$dim})=>$dim};
  --ch-color:${({$color})=>$color};
  --ch-accent:${({$accent})=>$accent};
`;

/* Location silhouette — right side */
const LocationSilhouette = styled.div`
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 140px;
  height: 190px;
  z-index: 0;
  pointer-events: none;
  animation: ${locationFloat} 6s ease-in-out infinite, ${locationGlow} 4s ease-in-out infinite;

  & svg { width: 100%; height: 100%; }

  @media(max-width:768px) {
    right: -15px;
    width: 90px;
    height: 120px;
    opacity: 0.3;
  }
`;

/* Location badge — top right */
const LocationBadge = styled.div<{$color:string}>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: "JetBrains Mono", monospace;
  font-size: 0.52rem;
  color: ${({$color}) => $color};
  padding: 0.3rem 0.7rem;
  background: ${({$color}) => $color}10;
  border: 1px solid ${({$color}) => $color}25;
  border-radius: 100px;
  z-index: 2;
  box-shadow: 0 0 12px ${({$color}) => $color}08;

  @media(max-width:600px) { display: none; }
`;

const LocationBadgeName = styled.span`
  letter-spacing: 0.1em;
  font-weight: 600;
`;

const SeeMore = styled(Link)`display:inline-block;font-family:"JetBrains Mono",monospace;font-size:0.65rem;color:var(--ch-color,#c8a050);text-decoration:none;&:hover{text-decoration:underline;}`;
const SeeMoreExt = styled.a`display:inline-block;font-family:"JetBrains Mono",monospace;font-size:0.65rem;color:var(--ch-color,#c8a050);text-decoration:none;&:hover{text-decoration:underline;}`;

const GlimpseFooter = styled.div`display:flex;align-items:center;justify-content:space-between;margin-top:1rem;padding-top:0.6rem;border-top:1px solid rgba(255,255,255,0.04);`;
const GlimpseCount = styled.span`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:var(--ch-dim,#555);`;

const ProjectPreview = styled.div`padding:1.2rem;background:rgba(255,255,255,0.05);border:1px solid var(--ch-color,#666)15;border-radius:12px;`;
const ProjectPreviewTitle = styled.h3`font-family:"Cinzel",serif;font-size:1rem;font-weight:700;color:var(--ch-text,#e0e0e0);margin-bottom:0.4rem;`;
const ProjectPreviewDesc = styled.p`font-family:"Raleway",sans-serif;font-size:0.75rem;color:var(--ch-muted,#888);line-height:1.6;margin-bottom:0.6rem;`;
const ProjectPreviewTech = styled.div`display:flex;flex-wrap:wrap;gap:0.3rem;margin-bottom:0.6rem;`;
const TechPill = styled.span`font-family:"JetBrains Mono",monospace;font-size:0.48rem;padding:0.15rem 0.4rem;border-radius:20px;background:var(--ch-color,#c8a050)08;border:1px solid var(--ch-color,#c8a050)15;color:var(--ch-color,#c8a050);`;
const ProjectPreviewLinks = styled.div`display:flex;gap:0.6rem;`;
const ProjectLink = styled.a<{$accent?:boolean}>`font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:${({$accent})=>$accent?"var(--ch-accent,#f0c860)":"var(--ch-muted,#888)"};text-decoration:none;&:hover{text-decoration:underline;}`;

/* ===== Cards use CSS vars for theming ===== */
const CurrentCard = styled.div`display:flex;align-items:flex-start;gap:1rem;padding:1.2rem;background:rgba(255,255,255,0.05);border:1px solid var(--ch-color,#666)15;border-radius:12px;@media(max-width:600px){flex-direction:column;}`;
const CurrentLogo = styled.div`font-size:2rem;flex-shrink:0;display:flex;align-items:center;color:#fa0f00;`;
const CurrentInfo = styled.div`flex:1;`;
const CurrentCompany = styled.h3`font-family:"Cinzel",serif;font-size:1.1rem;font-weight:700;color:var(--ch-text,#e0e0e0);`;
const CurrentRole = styled.p`font-family:"Raleway",sans-serif;font-size:0.82rem;color:var(--ch-muted,#888);margin-top:0.1rem;`;
const CurrentTeam = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.58rem;color:var(--ch-dim,#555);margin-top:0.15rem;`;
const CurrentHighlight = styled.p`font-family:"Raleway",sans-serif;font-size:0.75rem;color:var(--ch-muted,#888);margin-top:0.5rem;line-height:1.5;`;
const LiveBadge = styled.div`display:flex;align-items:center;gap:0.3rem;font-family:"JetBrains Mono",monospace;font-size:0.55rem;color:var(--ch-color,#c8a050);padding:0.25rem 0.6rem;background:var(--ch-color,#c8a050)08;border:1px solid var(--ch-color,#c8a050)15;border-radius:20px;flex-shrink:0;`;
const LiveDot = styled.div`width:5px;height:5px;border-radius:50%;background:var(--ch-color,#c8a050);animation:${livePulse} 2s ease-in-out infinite;`;

const ExpertiseGrid = styled.div`display:grid;grid-template-columns:repeat(2,1fr);gap:0.8rem;@media(max-width:600px){grid-template-columns:1fr;}`;
const ExpertiseCard = styled.div`padding:1rem;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.05);border-radius:10px;transition:all 0.3s ease;&:hover{border-color:var(--ch-color,#666)30;transform:translateY(-2px);}`;
const ExpIcon = styled.div`font-size:1.2rem;margin-bottom:0.4rem;`;
const ExpTitle = styled.h4`font-family:"Cinzel",serif;font-size:0.82rem;font-weight:600;color:var(--ch-text,#ccc);margin-bottom:0.2rem;`;
const ExpDesc = styled.p`font-family:"Raleway",sans-serif;font-size:0.7rem;color:var(--ch-dim,#666);line-height:1.5;`;

const AwardsList = styled.div`display:flex;flex-direction:column;gap:0.5rem;`;
const AwardItem = styled.div<{$i:number}>`display:flex;align-items:flex-start;gap:0.8rem;padding:0.8rem;background:rgba(255,255,255,${({$i})=>$i%2===0?"0.05":"0.03"});border-radius:8px;transition:all 0.25s ease;&:hover{background:rgba(255,255,255,0.06);}`;
const AwardIcon = styled.div`font-size:1.3rem;flex-shrink:0;`;
const AwardContent = styled.div`flex:1;`;
const AwardTitle = styled.h4`font-family:"Cinzel",serif;font-size:0.8rem;font-weight:600;color:var(--ch-text,#ccc);`;
const AwardMeta = styled.p`font-family:"JetBrains Mono",monospace;font-size:0.52rem;color:var(--ch-dim,#555);margin-top:0.1rem;`;

const BlogCard = styled.a`display:block;padding:1rem;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.05);border-radius:10px;text-decoration:none;transition:all 0.3s ease;&:hover{border-color:var(--ch-color,#666)20;transform:translateY(-2px);}`;
const BlogTag = styled.span`font-family:"JetBrains Mono",monospace;font-size:0.5rem;padding:0.15rem 0.4rem;border-radius:20px;display:inline-block;margin-bottom:0.5rem;`;
const BlogTitle = styled.h4`font-family:"Cinzel",serif;font-size:0.78rem;font-weight:600;color:var(--ch-text,#ccc);margin-bottom:0.3rem;line-height:1.4;`;
const BlogDesc = styled.p`font-family:"Raleway",sans-serif;font-size:0.65rem;color:var(--ch-dim,#555);line-height:1.5;margin-bottom:0.4rem;`;
const BlogLink = styled.span`font-family:"JetBrains Mono",monospace;font-size:0.5rem;`;

/* ===== Activity Heatmap ===== */
const ActWrap = styled.div<{$visible:boolean}>`
  max-width: 800px;
  margin: 2rem auto 3rem;
  padding: 2rem;
  background: rgba(20, 16, 10, 0.6);
  border: 1px solid rgba(180, 140, 50, 0.08);
  border-radius: 16px;
  opacity: ${({$visible}) => $visible ? 1 : 0};
  transform: translateY(${({$visible}) => $visible ? 0 : "25px"});
  transition: opacity 0.7s ease, transform 0.7s ease;
`;

const ActHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ActLeft = styled.div``;
const ActTag = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #6a5a3a;
  letter-spacing: 0.25em;
  margin-bottom: 0.3rem;
`;
const ActTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #e0d8c8;
`;
const ActRight = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: #7a6a4a;
  & b { color: #d0c0a0; font-size: 0.85rem; }
`;

const HeatmapScroll = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const ActSvg = styled.svg`
  width: 100%;
  min-width: 700px;
  height: auto;
  display: block;
`;

const ActFooter = styled.div`
  margin-top: 1rem;
  text-align: right;
`;

const ContactText = styled.p`font-family:"Raleway",sans-serif;font-size:0.88rem;color:var(--ch-muted,#777);line-height:1.7;margin-bottom:1.5rem;max-width:500px;`;
const ContactButtons = styled.div`display:flex;gap:0.8rem;flex-wrap:wrap;margin-bottom:1rem;`;
const ContactBtn = styled.a<{$secondary?:boolean}>`font-family:"JetBrains Mono",monospace;font-size:0.72rem;padding:0.6rem 1.2rem;border-radius:8px;text-decoration:none;transition:all 0.3s ease;
  ${({$secondary})=>$secondary
    ?`color:var(--ch-dim,#555);border:1px solid var(--ch-color,#666)15;background:transparent;&:hover{border-color:var(--ch-color,#666)30;color:var(--ch-text,#ccc);}`
    :`color:var(--ch-text,#ccc);background:var(--ch-color,#666)08;border:1px solid var(--ch-color,#666)15;&:hover{background:var(--ch-color,#666)15;transform:translateY(-2px);}`}
`;

/* ===================== CYLINDER ROLLER ===================== */
const rollerIn = keyframes`from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}`;

const RollerWrap = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 100;
  animation: ${rollerIn} 0.4s ease both;

  @media (max-width: 768px) {
    bottom: 12px;
    right: 12px;
  }
`;

const RollerWindow = styled.div`
  position: relative;
  width: 160px;
  height: 100px;
  overflow: hidden;
  background: rgba(12, 10, 8, 0.92);
  border: 1px solid rgba(180, 140, 50, 0.1);
  border-radius: 14px;
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &::before, &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 25px;
    z-index: 2;
    pointer-events: none;
  }
  &::before {
    top: 0;
    background: linear-gradient(180deg, rgba(12,10,8,0.95), transparent);
    border-radius: 14px 14px 0 0;
  }
  &::after {
    bottom: 0;
    background: linear-gradient(0deg, rgba(12,10,8,0.95), transparent);
    border-radius: 0 0 14px 14px;
  }
`;

const RollerItem = styled.div<{$offset:number; $active:boolean; $near:boolean; $visible:boolean; $color:string}>`
  position: absolute;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.8rem;
  height: 32px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;

  top: 50%;
  transform: translateY(calc(-50% + ${({$offset}) => $offset * 34}px))
    scale(${({$active}) => $active ? 1 : 0.85})
    perspective(200px)
    rotateX(${({$offset}) => $offset * -15}deg);
  opacity: ${({$visible}) => $visible ? 1 : 0};
  pointer-events: ${({$visible}) => $visible ? "auto" : "none"};
  z-index: ${({$active}) => $active ? 3 : 1};

  ${({$active, $color}) => $active && `
    background: ${$color}12;
    border-radius: 8px;
    box-shadow: 0 0 12px ${$color}15;
  `}

  &:hover {
    background: rgba(255,255,255,0.04);
    border-radius: 8px;
  }
`;

const RollerEmoji = styled.span`
  font-size: 0.85rem;
  flex-shrink: 0;
  width: 22px;
  text-align: center;
`;

const RollerLabel = styled.span<{$active:boolean}>`
  font-family: "JetBrains Mono", monospace;
  font-size: ${({$active}) => $active ? "0.58rem" : "0.48rem"};
  color: ${({$active}) => $active ? "#e0d8c8" : "#5a5040"};
  letter-spacing: 0.08em;
  font-weight: ${({$active}) => $active ? 600 : 400};
  transition: all 0.3s ease;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RollerNum = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.42rem;
  color: #3a3020;
  flex-shrink: 0;
`;
